import axios from "axios";

export type HeritageAutoLinkAmbiguity = {
  item_id: number;
  item_name: string | null;
  heritage_part_number: string;
  reason: string;
};

export type HeritageAutoLinkResult = {
  linked: number;
  ambiguous: HeritageAutoLinkAmbiguity[];
  alreadyLinked: number;
  unmatched: number;
};

/**
 * Link downloaded Heritage parts to the company's own items.
 *
 * Only unambiguous matches are linked; anything with more than one candidate
 * comes back for a person to confirm, because a wrong link puts the wrong part
 * on a real purchase order.
 */
export async function runHeritageAutoLink(vendorId: number): Promise<HeritageAutoLinkResult> {
  const { data } = await axios.post<HeritageAutoLinkResult>(
    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/auto-link`,
    { vendorId }
  );
  return data;
}

/**
 * A sentence describing what the run did, for the panel to show.
 *
 * Every outcome is named, including the ones that need attention: silently
 * reporting only the successes would hide the items a person still has to
 * confirm, and the catalog parts that matched nothing at all.
 */
export function describeAutoLinkResult(result: HeritageAutoLinkResult): string {
  const parts: string[] = [];

  parts.push(
    result.linked === 1 ? "1 item linked automatically" : `${result.linked} items linked automatically`
  );

  if (result.ambiguous.length > 0) {
    parts.push(
      result.ambiguous.length === 1
        ? "1 needs you to confirm which part it is"
        : `${result.ambiguous.length} need you to confirm which part they are`
    );
  }

  if (result.alreadyLinked > 0) {
    parts.push(`${result.alreadyLinked} already linked`);
  }

  if (result.unmatched > 0) {
    parts.push(`${result.unmatched} catalog parts had no match in your items`);
  }

  return `${parts.join(", ")}.`;
}
